import React, {Fragment, useContext, useState} from 'react';
import {bool, func, number, object, oneOfType, shape, string} from 'prop-types';
import {useHistory, useLocation} from 'react-router-dom';
import {injectIntl, intlShape} from 'react-intl';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {length, find, path, pathOr, split} from 'ramda';
import classNames from 'classnames';
import {
  getEstimatedBreakdownMaybe,
  getPrices,
  getPricingDataFromEntity,
} from '@swimmy_/helpers';

import routeConfiguration from '../../routeConfiguration';
import {CUSTOMER} from '../../constants/booking';
import {LISTING_STATE_CLOSED} from '../../constants/listing';
import {CHECKOUT_EXTRA_PAGE, CHECKOUT_PAGE, CHECKOUT_PROPOSAL_PAGE} from '../../constants/page';
import {types as sdkTypes} from '../../util/sdkLoader';
import {propTypes} from '../../util/types';
import {withViewport} from '../../util/contextHelpers';
import {createResourceLocatorString, findRouteByRouteName} from '../../util/routes';
import {createSlug, parse} from '../../util/urlHelpers';
import {formatMoney, unitDivisor} from '../../util/currency';
import {formatFormValuesAsBookingData, getInitialValuesFromSearch} from '../../util/booking';
import {manageDisableScrolling, isScrollingDisabled} from '../../ducks/UI.duck';
import {fetchTimeSlots} from '../../ducks/listing.duck';
import {initializeCardPaymentData} from '../../ducks/stripe.duck.js';
import {
  IconReviewStar,
  InstantBookingBadge,
  ModalInMobile,
  PriceBreakdown,
  PrimaryButton,
} from '../../components';

import {
  MODAL_BREAKPOINT,
  closeBookModal,
  openBookModal,
  getInitialValuesFromTransaction,
} from './BookingPanel.helpers';
import BookingForm from './BookingForm/BookingForm';
import css from './BookingPanel.module.css';
import {isInstantBooking} from '../../util/transactions';
import {addToCart, markDatadogRumCustomerLeadReserveClickEligible} from '../../ducks/user.duck';
import {getHours, moment} from '../../util/dates';
import SuggestMultiBookingModal from './SuggestMultiBookingModal/SuggestMultiBookingModal';
import {isNilOrEmpty} from '../../util/validators';
import {getSearchLocation} from "../../util/search";
import {BookingRequestContext} from "../../containers/ListingPage/hooks/useBookingRequest";
import BoostYourListings from "../BoostYourListings";

const {Money} = sdkTypes;

const TODAY = new Date();

const dateFormattingOptions = {month: 'short', day: 'numeric', weekday: 'short'};

const BookingPanel = (props) => {
  const {
    cartBookingSearchId,
    hasItemsInCart,
    isInCart,
    isAuthenticated,
    isComingFromSearch,
    rootClassName,
    className,
    onAddToCart,
    callSetInitialValues,
    currentUser,
    intl,
    isOwnListing,
    listing,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onInitializeCardPaymentData,
    onMarkDatadogRumCustomerLeadReserveClick,
    onManageDisableScrolling,
    rating,
    searchParams,
    transaction,
    viewport,
    withoutBorder,
    searchQuery,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const {
    setFormValues,
    formValues,
  } = useContext(BookingRequestContext);
  const [isOpenSuggestMultiBookingModal, setIsOpenSuggestMultiBookingModal] = useState(false);
  const [isAddingToCartInProgress, setIsAddingToCartInProgress] = useState(false);
  const {
    adults = 1,
    bookingDate,
    children = 0,
    endTime,
    startTime,
    proposalPrice,
  } = formValues || {};
  const timeZone = listing.attributes.availabilityPlan && listing.attributes.availabilityPlan.timezone;
  const isMobile = viewport.width <= MODAL_BREAKPOINT;
  const hasListingState = !!listing.attributes.state;
  const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;
  const showBookingForm = hasListingState && !isClosed;
  const isBook = !!parse(location.search).book;
  const publicData = pathOr({}, ['attributes', 'publicData'], listing);
  const price = pathOr({}, ['attributes', 'price'], listing);
  const {paidExtrasData = [], pricePerExtraGuest, pricingBaseNumberOfPeople} = publicData;
  const pricingData = getPricingDataFromEntity(listing);
  const searchLocation = getSearchLocation(searchParams);
  const getQuantityToNumberOfPeopleFormat = (quantity = {}) => ({
    "numberOfAdults": quantity.adults,
    "numberOfChildren": quantity.children,
    "numberOfInfants": quantity.infants,
  });
  const getBookingSearchId = (numberOfPeople, start, end) =>
    `${JSON.stringify(searchLocation)}_${start}_${end}_${JSON.stringify(numberOfPeople)}`;
  const getBookingParams = (bookingData) => {
    if (!bookingData) return {};

    const start = moment(bookingData.bookingStart);
    const end = moment(bookingData.bookingEnd);
    return {
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      nbHoursSelected: Math.abs(getHours(end) - getHours(start)),
      numberOfPeople: {
        "numberOfAdults": bookingData.quantity.adults,
        "numberOfChildren": bookingData.quantity.children,
        "numberOfInfants": bookingData.quantity.infants,
      },
    };
  };
  const canEstimatePrice = bookingDate && startTime && endTime && startTime !== endTime && adults >= 1;
  const speculativePrices = getPrices(listing.attributes, {
    bookingDate,
    startTime,
    endTime,
    adults,
    children,
  });
  const totalText = speculativePrices?.secondaryPrice && intl.formatMessage(
    {id: 'ListingCard.ListingPrice.total'},
    {price: formatMoney(intl, speculativePrices.secondaryPrice, true)},
  );
  const bookingData = canEstimatePrice && {
    ...formatFormValuesAsBookingData(formValues),
    price,
    pricePerExtraGuest,
    pricingBaseNumberOfPeople,
    publicData,
  };
  const handleOpenSuggestMultiBookingModal = (goBackTwice) => {
    if (isNilOrEmpty(props.searchParams) || applyInstantBooking || proposalPrice) {
      handleSubmit();
      return;
    }
    if (!isAuthenticated) {
      history.push({
        pathname: createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}),
        state: {from: `${location?.pathname ?? ''}${location?.search ?? ''}`, canGoBack: true}
      })
      return;
    }
    if (isInCart && isSameSearchBookingParamsAsCurrentBookingParams) {
      history.goBack();
      if (goBackTwice) {
        history.goBack();
      }
      return;
    }
    if (hasItemsInCart) {
      handleSubmitMultiBooking(goBackTwice);
      return;
    }

    setIsOpenSuggestMultiBookingModal(true);
  };
  const handleOpenBookModalMaybe = () => {
    if (isInCart && isSameSearchBookingParamsAsCurrentBookingParams) {
      history.goBack();
      return;
    }
    if (hasItemsInCart && canEstimatePrice && !isMobile) {
      handleOpenSuggestMultiBookingModal(false);
      return;
    }

    openBookModal(isOwnListing, isClosed, history, location);
  };
  const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  const handleSubmitMultiBooking = async (goBackTwice, isFromModal) => {
    setIsAddingToCartInProgress(true);
    await onAddToCart(listing.id.uuid, {
      bookingSearchId,
      bookingParams,
      isSameSearchBookingParamsAsCurrentBookingParams,
    });
    if (isFromModal)
      await sleep(1000);
    setIsAddingToCartInProgress(false);
    setIsOpenSuggestMultiBookingModal(false);
    if (!isSameSearchBookingParamsAsCurrentBookingParams || !isComingFromSearch) {
      setTimeout(() => {
        const routes = routeConfiguration();
        history.push(
          createResourceLocatorString(
            'SearchPage',
            routes,
            {},
            {
              ...searchParams,
              ...bookingData.quantity,
              bookingDate: moment(bookingData.bookingStart).set({hour: 0}).toISOString(),
              startTime: moment(bookingData.bookingStart).format('HH:mm'),
              endTime: moment(bookingData.bookingEnd).format('HH:mm'),
            },
          ),
        );
      }, 100);
      return;
    }

    history.goBack();
    if (goBackTwice) {
      history.goBack();
      history.goBack();
    }
  };

  const estimatedTransaction = canEstimatePrice && getEstimatedBreakdownMaybe({
    uuid: listing.id.uuid,
    ...pricingData,
    bookingStart: bookingData.bookingStart,
    bookingEnd: bookingData.bookingEnd,
    quantity1: bookingData.quantity.adults,
    quantity2: bookingData.quantity.children,
    quantityHours: bookingData.quantityHours,
    publicData,
    protectedData: pricingData,
    canApplyCommissionDiscountOnBigBookings: false,
    isTransactionHourlyPlan: true,
  });
  const {payinTotal} = pathOr({}, ['attributes'], estimatedTransaction);
  const estimatedPrice = canEstimatePrice &&
    new Money(payinTotal.amount, payinTotal.currency);
  const canMakeProposal = adults >= 10 && payinTotal?.amount >= 50000;
  const proposalPriceMin = (canEstimatePrice && canMakeProposal) &&
    new Money(payinTotal.amount * 0.6, payinTotal.currency);
  const formInitialValues =
    transaction ?
      getInitialValuesFromTransaction(transaction) :
      getInitialValuesFromSearch(searchParams);
  const instantBookingFrom = path(['attributes', 'publicData', 'instantBookingFrom'], listing);
  const applyInstantBooking = isInstantBooking(instantBookingFrom, estimatedTransaction);

  const priceUnitText = intl.formatMessage({id: 'Marketplace.perHourPerUnitShort'});
  const noPriceText = intl.formatMessage({id: 'BookingPanel.noPrice'});

  const payinTotalAsMoney = canEstimatePrice && new Money(payinTotal.amount, payinTotal.currency);

  const formattedEstimatedPrice = canEstimatePrice ? formatMoney(intl, payinTotalAsMoney, true) : '';
  const bookingParams = getBookingParams(bookingData);
  const initialSearchBookingParams = formInitialValues && getBookingParams(formatFormValuesAsBookingData(formInitialValues));
  const bookingSearchId = bookingData && getBookingSearchId(getQuantityToNumberOfPeopleFormat(bookingData.quantity), bookingData.bookingStart.toISOString(), bookingData.bookingEnd.toISOString());
  const initialBookingSearchId = initialSearchBookingParams && getBookingSearchId(initialSearchBookingParams.numberOfPeople, initialSearchBookingParams.startAt, initialSearchBookingParams.endAt);
  const isSameSearchBookingParamsAsCurrentBookingParams = bookingSearchId === initialBookingSearchId && (!cartBookingSearchId || bookingSearchId === cartBookingSearchId);
  const headlineText = intl.formatMessage({id: `BookingPanel.${isOwnListing ? 'ownListing' : isClosed ? 'closed' : 'headline'}`});
  const bookButtonText = intl.formatMessage({id: `BookingPanel.${canEstimatePrice ? 'book' : 'bookWithoutData'}`});
  const selectButtonText = intl.formatMessage({id: 'BookingPanel.selectButton'});
  const seeOrderText = intl.formatMessage({id: 'BookingPanel.SeeOrder'});
  const noPaymentYetText = intl.formatMessage({id: 'BookingPanel.BookingForm.noPaymentYet'});
  const canEstimateText = intl.formatMessage({id: 'BookingPanel.BookingForm.canEstimate'});
  const classes = classNames(css.root, className);
  const containerClasses = classNames(css.modalContainer, {[css.modalContainerBorder]: !withoutBorder});

  const handleSubmit = () => {
    onMarkDatadogRumCustomerLeadReserveClick();
    const initialValues = {
      listing,
      transaction,
      bookingData: {
        ...bookingData,
        proposalPrice: proposalPrice * unitDivisor(payinTotal.currency),
      },
      bookingDates: {
        bookingStart: bookingData.bookingStart.toDate(),
        bookingEnd: bookingData.bookingEnd.toDate(),
      },
      confirmPaymentError: null,
    };
    const saveToSessionStorage = !currentUser;
    const routes = routeConfiguration();
    const listingHasExtras = paidExtrasData.length > 0;
    const nextRouteName = canMakeProposal
      ? CHECKOUT_PROPOSAL_PAGE
      : listingHasExtras
        ? CHECKOUT_EXTRA_PAGE
        : CHECKOUT_PAGE;
    const {setInitialValues} = findRouteByRouteName(nextRouteName, routes);
    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);
    if (!listingHasExtras && !canMakeProposal) {
      onInitializeCardPaymentData();
    }
    history.push(
      createResourceLocatorString(
        nextRouteName,
        routes,
        {id: listing.id.uuid, slug: createSlug(listing.attributes.title)},
        {}
      )
    );
  };

  return (
    <div className={classes}>
      {isOwnListing && !isMobile && (
        <div className={css.boostYourListingsContainer}>
          <BoostYourListings isLight/>
        </div>
      )}
      <SuggestMultiBookingModal
        isOpen={isOpenSuggestMultiBookingModal}
        intl={intl}
        inProgress={isAddingToCartInProgress}
        onCloseModal={() => handleSubmitMultiBooking(isMobile, true)}
        onSubmit={() => handleSubmitMultiBooking(isMobile, true)}
        onManageDisableScrolling={Function.prototype}
        listingCover={path(['images', 0, 'attributes', 'variants', 'square-small', 'url'], listing)}
      />
      <ModalInMobile
        hiddenClassName={css.modalInMobileHidden}
        containerClassName={containerClasses}
        contentClassName={css.modalContent}
        id="BookingFormInModal"
        isModalOpenOnMobile={isBook}
        onClose={() => closeBookModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.header}>
          <div className={css.priceWrapper}>
            {speculativePrices.mainPrice ? (
              <div className={css.basePriceWrapper}>
                <span
                  className={css.price}>{formatMoney(intl, speculativePrices.mainPrice, true)}</span>
                <span className={css.priceUnit}>{priceUnitText}</span>
              </div>
            ) : (
              <span className={css.noPrice}>{noPriceText}</span>
            )}
          </div>
          {rating > 4 && (
            <div className={css.reviewWrapper}>
              <IconReviewStar className={css.iconReviewStar} isFilled/>
              <span className={css.rating}>{rating}</span>
            </div>
          )}
        </div>
        {!!showBookingForm && (
          <Fragment>
            <BookingForm
              searchQuery={searchQuery}
              className={css.bookingForm}
              exposeFormValues={setFormValues}
              formattedPrice={formattedEstimatedPrice}
              estimatedPrice={estimatedPrice || null}
              proposalPriceMin={proposalPriceMin || null}
              formId="bookingForm"
              initialValues={formInitialValues}
              intl={intl}
              isInCart={isInCart}
              hasItemsInCart={hasItemsInCart}
              isSameSearchBookingParamsAsCurrentBookingParams={isSameSearchBookingParamsAsCurrentBookingParams}
              applyInstantBooking={applyInstantBooking}
              onSubmit={() => handleOpenSuggestMultiBookingModal(isMobile)}
              publicData={publicData}
              submitButtonWrapperClassName={css.submitButtonWrapper}
              price={price}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
            />
            {canEstimatePrice && (
              <Fragment>
                {applyInstantBooking && <InstantBookingBadge className={css.instantBookingBadge}/>}
                <span className={css.noPaymentYet}>{noPaymentYetText}</span>
                <span className={css.noPaymentYet}>{canEstimateText}</span>
                <PriceBreakdown
                  className={css.priceBreakdown}
                  transaction={estimatedTransaction}
                  userRole={CUSTOMER}
                  expandable
                />
              </Fragment>
            )}
          </Fragment>
        )}
      </ModalInMobile>
      {isMobile && (
        <div className={css.buttonContainer}>
          {canEstimatePrice ? (
            <div>
              <div className={css.basePriceWrapper}>
              <span
                className={css.priceMobile}>{formatMoney(intl, speculativePrices.mainPrice, true)}</span>
                <span className={css.priceUnitMobile}>{priceUnitText}</span>
              </div>
              {totalText && <div
                className={css.totalPrice}>{totalText}</div>}
            </div>
          ) : (
            <span className={css.book}>{noPriceText}</span>
          )}
          <PrimaryButton
            className={css.bookButton}
            disabled={isOwnListing || !showBookingForm}
            onClick={handleOpenBookModalMaybe}
          >
            {canMakeProposal ? (
              intl.formatMessage({id: 'BookingPanel.BookingForm.MakeAnOffer'})
            ) : (
              <>
                {isInCart && seeOrderText}
                {!isInCart && hasItemsInCart && selectButtonText}
                {!isInCart && !hasItemsInCart && bookButtonText}
              </>
            )}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

BookingPanel.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  isOwnListing: false,
  rating: null,
  searchParams: {},
  monthlyTimeSlots: null,
  transaction: null,
  withoutBorder: false,
};

BookingPanel.propTypes = {
  rootClassName: string,
  className: string,
  callSetInitialValues: func.isRequired,
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  titleClassName: string,
  isOwnListing: bool,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  monthlyTimeSlots: object,
  onInitializeCardPaymentData: func.isRequired,
  onMarkDatadogRumCustomerLeadReserveClick: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onFetchTimeSlots: func.isRequired,
  rating: number,
  scrollingDisabled: bool.isRequired,
  searchParams: object,
  transaction: propTypes.transaction,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  withoutBorder: bool,
};

const mapStateToProps = (state, props) => {
  const {monthlyTimeSlots} = state.listing;
  const {currentUser} = state.user;
  const listingId = pathOr('', ['listing', 'id', 'uuid'], state);
  const addToCart = pathOr([], ['attributes', 'profile', 'publicData', 'addToCart'], currentUser);
  const isComingFromSearch = !isNilOrEmpty(props.searchParams) && !!props.isFromSearch;
  const isInCart = length(addToCart) > 0 && find(lid => lid === listingId, addToCart) && isComingFromSearch;
  const {isAuthenticated} = state.Auth;
  const cartBookingSearchId = isAuthenticated && pathOr(null, ['attributes', 'profile', 'privateData', 'cartBookingSearchId'], currentUser);

  return {
    currentUser,
    cartBookingSearchId,
    hasItemsInCart: length(addToCart) > 0 && !isNilOrEmpty(props.searchParams),
    isComingFromSearch,
    isInCart,
    monthlyTimeSlots,
    scrollingDisabled: isScrollingDisabled(state),
    isAuthenticated,
  };
};

const mapDispatchToProps = dispatch => ({
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onMarkDatadogRumCustomerLeadReserveClick: () =>
    dispatch(markDatadogRumCustomerLeadReserveClickEligible()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onAddToCart: (listingId, params) => dispatch(addToCart(listingId, params)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withViewport,
  injectIntl,
)(BookingPanel);
